import { useRef, useState } from "react"
import { Check, Headphones, Pause, Play, RotateCcw, UserPlus } from "lucide-react"
import { Link } from "@inertiajs/react"

import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import StatusBadge from "../StatusBadge"
import { Avatar } from "../Avatar"
import { Button } from "@/components/ui/button"
import { ORDER_MODAL_KEYS } from "./actions"
import { cn } from "@/lib/utils"

// Copy per server-derived narration state (AU-30). The panel never infers
// production from an empty chapter array — it reads order.narration.state,
// which the presenter derives from the narrator assignment plus the order
// phase. Narration keeps three separate file stores and the narrator's first
// submission is a preview mp3, not a chapter, so "no chapters" says nothing
// about whether recording has started.
const STATE_COPY = {
  // Payment outranks assignment in narration_state, so this state is reached
  // with or without a narrator — and dropping one is only permitted while
  // awaiting payment, which makes the unassigned shape the common one. The copy
  // must not assert an assignment that may not exist.
  awaitingPayment: {
    heading: "Payment is needed before recording can begin",
    body: "Recording starts once this project is paid.",
  },
  awaitingPaymentAssigned: {
    heading: "Payment is needed before recording can begin",
    body: "Your narrator is assigned, but recording starts once this project is paid.",
  },
  awaitingContract: {
    heading: "Your narrator is getting started",
    body: "Your narrator has been selected and is finalizing their contract. Recording begins after that's complete.",
  },
  assignedPreProduction: {
    heading: "Your narrator is assigned",
    body: "Your narrator has been selected. Recording hasn't started yet.",
  },
  // In Narration before any sample exists — either the first one or a
  // replacement after the customer asked for another. Recording the book has
  // not started, so this must not claim it has.
  awaitingSample: {
    heading: "Your narrator is preparing a sample",
    body: "You'll hear a short sample so you can check the voice and delivery before they record the rest of your book.",
  },
  recording: {
    heading: "Recording is under way",
    body: "Your narrator is in the studio. Chapter takes appear here for you to listen to and approve as they're recorded.",
  },
  // Reached only if a final-review order somehow carries no chapter rows; the
  // normal path renders the chapter list instead. Without its own entry this
  // fell back to claiming recording was still under way.
  chapterReview: {
    heading: "Your narrator has submitted their recording",
    body: "The finished chapters are being prepared for you to review here.",
  },
  inRevision: {
    heading: "Your narrator is reworking a take",
    body: "You asked for changes. The updated recording will appear here once it's re-recorded.",
  },
  finalizing: {
    heading: "Your audiobook is being packaged",
    body: "Recording is approved and your narrator is preparing the final files for delivery.",
  },
}

function fmtTime(seconds) {
  if (!seconds || Number.isNaN(seconds)) return "—"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, "0")}`
}

// Audio tab — the real per-chapter narration takes from S3 (narration_files),
// played inline. A narration order leads with this tab instead of "Document".
// Until recording starts it shows the not-ready state. Mirrors order-audio.html.
export default function AudioPanel({ order, onOpenModal, onSelectTab }) {
  const chapters = order.deliverables?.audioChapters ?? []
  const nar = order.narration ?? {}
  const state = nar.state ?? (nar.narratorAssigned ? "recording" : "unassigned")
  const assigned = Boolean(nar.narratorAssigned)
  const narrator = nar.narrator ?? order.team?.[0]
  const sample = nar.sample
  const audioRef = useRef(null)
  const [currentId, setCurrentId] = useState(chapters[0]?.id ?? null)
  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState(null)
  const messageNarrator = () => onSelectTab?.("chat")
  // A collaborator's read-only view (AU-44): the empty states keep their
  // message but drop the control, which leads to the owner's chat or support.
  const readOnly = Boolean(order.viewer?.readOnly)

  if (!chapters.length) {
    // Gated on the server state, never on the attachment. narration_audio_sample
    // is ALSO written when a narrator is assigned — FreelancerAssigned copies the
    // audition sample from the request onto the order — and dropping a narrator
    // clears the writer without purging it. Keying on presence alone would show
    // "your narrator recorded a sample" on an order awaiting a contract, or on
    // one with no narrator at all.
    if (state === "sampleReview" && sample) {
      return (
        <PanelCard>
          <PanelCardHead Icon={Headphones} title="Narration sample">
            {narrator && (
              <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
                Narrated by <Avatar initials={narrator.initials} name={narrator.name} size={22} />
                <b className="font-semibold text-foreground">{narrator.name}</b>
              </span>
            )}
          </PanelCardHead>
          <PanelCardBody>
            <p className="mb-4 text-[12.5px] leading-[1.55] text-muted-foreground">
              Your narrator recorded a short sample so you can check the voice and delivery before they
              record the rest of your book.
            </p>
            <div className="rounded-2xl bg-gradient-to-br from-[#2A2622] to-[#14110F] px-5 py-4 text-[#F4F4F2]">
              <div className="truncate font-display text-[13.5px] font-bold">{sample.fileName || "Narration sample"}</div>
              <audio src={sample.url} className="mt-3 h-9 w-full" controls />
            </div>
          </PanelCardBody>

          {order.permissions?.reviewSample && (
            <div className="flex flex-wrap items-center gap-4 border-t border-border bg-gradient-to-b from-card to-accent px-[22px] py-4 max-[720px]:gap-3 max-[720px]:px-4 max-[720px]:py-[14px]">
              <div className="max-[720px]:basis-full">
                <h4 className="font-display text-sm font-bold">Happy with this voice?</h4>
                <p className="mt-0.5 text-[12px] text-ink-3">
                  Approving starts recording. You&apos;ll still review the finished chapters.
                </p>
              </div>
              <div className="ml-auto flex gap-[9px] max-[720px]:w-full">
                <Button
                  variant="outline"
                  className="gap-[7px] max-[720px]:flex-1"
                  onClick={() => onOpenModal?.("request-another-sample")}
                >
                  <RotateCcw className="size-4" strokeWidth={2} />
                  Request another
                </Button>
                <Button
                  className="gap-[7px] font-bold max-[720px]:flex-1"
                  onClick={() => onOpenModal?.("approve-sample")}
                >
                  <Check className="size-4" strokeWidth={2.2} />
                  Approve sample
                </Button>
              </div>
            </div>
          )}
        </PanelCard>
      )
    }

    // Cancelled, on hold or closed. Say so plainly rather than describing
    // recording that is not going to happen next.
    if (state === "inactive") {
      return (
        <PanelEmpty
          Icon={Headphones}
          title="Audiobook"
          heading="Recording isn't in progress"
          body={nar.statusLabel ? `This project is ${nar.statusLabel.toLowerCase()}.` : "This project isn't active."}
          action={
            assigned && !readOnly && (
              <Button variant="outline" size="sm" onClick={messageNarrator}>
                Message your narrator
              </Button>
            )
          }
        />
      )
    }

    // Chosen but not yet accepted — writer_id is still nil, so this must come
    // before the unassigned branch or the customer is sent back to browse.
    if (state === "awaitingNarratorResponse") {
      return (
        <PanelEmpty
          Icon={Headphones}
          title="Audiobook"
          heading="Waiting for your narrator to accept"
          body="You've chosen a narrator. Recording begins once they accept and their contract is complete."
        />
      )
    }

    // Unassigned: no narrator yet, so nothing is in production. Primary action
    // is Find Freelancers prefiltered for narrators; no "Message" affordance.
    if (state === "unassigned") {
      return (
        <PanelEmpty
          Icon={UserPlus}
          title="Audiobook"
          heading="Select your narrator"
          body="A narrator must be selected before recording can begin. Browse narrators and choose who you'd like to work with."
          action={
            !readOnly && (
              <Button asChild size="sm">
                <Link href="/find-freelancers?role=narrator">Find narrators</Link>
              </Button>
            )
          }
        />
      )
    }

    if (state === "awaitingPayment") {
      const copy = assigned ? STATE_COPY.awaitingPaymentAssigned : STATE_COPY.awaitingPayment
      return (
        <PanelEmpty
          Icon={Headphones}
          title="Audiobook"
          heading={copy.heading}
          body={copy.body}
          action={
            order.permissions?.payNow && (
              <Button size="sm" onClick={() => onOpenModal?.(ORDER_MODAL_KEYS.PAY_NOW)}>
                Pay now
              </Button>
            )
          }
        />
      )
    }

    // assignedOther: a narrator is assigned but the order sits outside the
    // enumerated sets. Render the real status label with NO production claim.
    if (state === "assignedOther") {
      return (
        <PanelEmpty
          Icon={Headphones}
          title="Audiobook"
          heading={narrator?.name ? `${narrator.name} is on your audiobook` : "Your narrator is assigned"}
          body={nar.statusLabel ? `Status: ${nar.statusLabel}` : "Your narrator is assigned to this project."}
          action={
            assigned && !readOnly && (
              <Button variant="outline" size="sm" onClick={messageNarrator}>
                Message your narrator
              </Button>
            )
          }
        />
      )
    }

    const copy = STATE_COPY[state] ?? STATE_COPY.recording
    return (
      <PanelEmpty
        Icon={Headphones}
        title="Audiobook"
        heading={copy.heading}
        body={copy.body}
        action={
          assigned && !readOnly && (
            <Button variant="outline" size="sm" onClick={messageNarrator}>
              Message your narrator
            </Button>
          )
        }
      />
    )
  }

  const current = chapters.find((c) => c.id === currentId) ?? chapters[0]
  // A chapter note is staged, not sent: it rides along with the revision the
  // customer submits from the header, which is what notifies the narrator. So
  // the note may only be written where that revision can still be made —
  // chapter review, with a revision left. That is NarrationFile
  // #can_request_changes?, said in the presenter's terms; offering it anywhere
  // else collected a note nothing would ever carry (AU-46).
  const canRequestChanges =
    !readOnly && state === "chapterReview" && !!order.permissions?.requestRevision

  const select = (chapter) => {
    setCurrentId(chapter.id)
    setDuration(null)
    // let the new src load, then play
    requestAnimationFrame(() => audioRef.current?.play().catch(() => {}))
  }

  const toggle = () => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => {})
    else el.pause()
  }

  return (
    <PanelCard>
      <PanelCardHead Icon={Headphones} title="Audiobook">
        <div className="flex items-center gap-3">
          {narrator && (
            <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
              Narrated by <Avatar initials={narrator.initials} name={narrator.name} size={22} />
              <b className="font-semibold text-foreground">{narrator.name}</b>
            </span>
          )}
          <StatusBadge tier="in_progress" label={`${chapters.length} ${chapters.length === 1 ? "take" : "takes"}`} />
        </div>
      </PanelCardHead>

      <PanelCardBody>
        {/* now-playing bar. Below 560 the transport drops to its own full-width
            line under the play button and the running time (order:3), rather
            than squeezing the native audio element into a sliver. */}
        <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-br from-[#2A2622] to-[#14110F] px-5 py-4 text-[#F4F4F2] max-[560px]:flex-wrap max-[560px]:gap-3 max-[560px]:px-4 max-[560px]:py-[15px]">
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? "Pause" : "Play"}
            className="grid size-12 shrink-0 place-items-center rounded-full bg-primary shadow-[0_8px_22px_-8px_var(--red-glow)]"
          >
            {playing ? <Pause className="size-5 fill-white text-white" /> : <Play className="ml-0.5 size-5 fill-white text-white" />}
          </button>
          <div className="min-w-0 flex-1 max-[560px]:order-3 max-[560px]:min-w-full">
            <div className="truncate font-display text-[13.5px] font-bold">{current.name}</div>
            <div className="mt-0.5 truncate text-[11.5px] opacity-60">{current.description || "Take ready to review"}</div>
            <audio
              ref={audioRef}
              src={current.file?.url}
              className="mt-2 h-8 w-full"
              controls
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            />
          </div>
          <div className="shrink-0 text-[11px] tabular-nums opacity-70">{fmtTime(duration)}</div>
        </div>

        <p className="mt-4 mb-3 text-[12.5px] leading-[1.55] text-muted-foreground">
          Listen to each chapter as it&apos;s recorded. When every take is right, approve the order from the header; if a chapter needs
          another pass, request changes and note what to adjust.
        </p>

        {/* chapter list */}
        <div className="overflow-hidden rounded-2xl border border-border">
          {chapters.map((chapter, i) => {
            const isCurrent = chapter.id === current.id
            return (
              <div
                key={chapter.id}
                className={cn(
                  "flex items-center gap-4 border-b border-border px-4 py-3.5 last:border-b-0",
                  "max-[560px]:flex-wrap max-[560px]:gap-x-3 max-[560px]:gap-y-2.5 max-[560px]:px-[14px] max-[560px]:py-[13px]",
                  isCurrent ? "bg-[var(--red-soft)]" : "bg-card hover:bg-accent"
                )}
              >
                <button
                  type="button"
                  onClick={() => select(chapter)}
                  aria-label={`Play ${chapter.name}`}
                  className="grid size-9 shrink-0 place-items-center rounded-[9px] border border-line-2 text-muted-foreground hover:text-foreground"
                  disabled={!chapter.file?.url}
                >
                  <Play className="ml-0.5 size-[15px] fill-current" />
                </button>
                <div className="min-w-0 flex-1 max-[560px]:min-w-[60%]">
                  <div className="truncate font-display text-[13.5px] font-semibold">
                    {i + 1}. {chapter.name}
                  </div>
                  {chapter.description && <div className="mt-0.5 truncate text-[11.5px] text-ink-3">{chapter.description}</div>}
                </div>
                {chapter.needsChanges ? (
                  <StatusBadge tier="needs_you" label="Changes requested" />
                ) : (
                  <div className="flex shrink-0 items-center gap-2.5">
                    <span className="text-[11.5px] text-ink-3">v{chapter.versionNumber ?? 1}</span>
                    {canRequestChanges && (
                      <button
                        type="button"
                        onClick={() => onOpenModal?.("chapter-changes", { chapterId: chapter.id, chapterName: chapter.name })}
                        className="flex items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1.5 text-[11.5px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
                      >
                        <RotateCcw className="size-[13px]" strokeWidth={2} />
                        Mark for changes
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </PanelCardBody>
    </PanelCard>
  )
}
